import { createAsyncThunk } from '@reduxjs/toolkit';
import { CatalogsProspectsResponse, ProspectResponse, ProspectResponseNote } from './prospects.types';
import { RootState } from '../RootState';
import * as API from 'src/apis/prospectsAPI';
import { CLOSED_TYPIFICATION_STATUSES, NOT_CLOSED_TYPIFICATION_STATUSES, STATUS_PROSPECTS } from 'src/components/Prospects/constants/status';

// Acción asíncrona para cargar prospectos
export type LoadProspectsThunkParams = { page?: number; withoutLoader?: boolean };
export const loadProspects = createAsyncThunk<
    ProspectResponse,
    LoadProspectsThunkParams,
    {
        state: RootState;
        rejectValue: string;
    }
>('prospects/loadProspects', async ({ page }: LoadProspectsThunkParams, thunkAPI) => {
    const numPage = page ?? 1;
    const {
        prospects: { selectedSection },
        User: { token },
    } = thunkAPI.getState();
    try {
        const defaultTypification =
            selectedSection.closed === STATUS_PROSPECTS.CLOSED
                ? Object.values(CLOSED_TYPIFICATION_STATUSES).join(',')
                : Object.values(NOT_CLOSED_TYPIFICATION_STATUSES).join(',');
        const typification = selectedSection.typification ?? defaultTypification;
        const query = `type=${selectedSection.type}&page=${numPage}&typification=${typification}${
            selectedSection.assigned !== null ? '&user_assigned_ids=' + selectedSection.assigned : ''
        }`;
        const response: ProspectResponse = await API.getProspects({ token, values: query });
        return response;
    } catch (error) {
        // Maneja otros errores
        return thunkAPI.rejectWithValue('Error de red');
    }
});

// Acción asíncrona para cargar catálogos de prospectos
export const loadProspectCatalogs = createAsyncThunk<CatalogsProspectsResponse, void, { state: RootState; rejectValue: string }>(
    'prospects/loadProspectCatalogs',
    async (_: void, thunkAPI) => {
        try {
            const {
                User: { token },
            } = thunkAPI.getState();
            const response: CatalogsProspectsResponse = await API.getProspectsCatalogs({ token });
            return response;
        } catch (error) {
            // Maneja otros errores
            return thunkAPI.rejectWithValue('Error de red');
        }
    }
);

// Accion asincrona para actualizar asignacion de prospecto
export const updateTypification = createAsyncThunk<
    ProspectResponse,
    { prospect_id: number; typification_id: number },
    {
        state: RootState;
        rejectValue: string;
    }
>('prospects/updateTypification', async ({ prospect_id, typification_id }, thunkAPI) => {
    try {
        const {
            User: { token },
            prospects: {
                pagination: { current_page },
            },
        } = thunkAPI.getState();

        const resp = await API.updateTypification({ token, prospect_id, typification_id });
        await thunkAPI.dispatch(loadProspects({ page: current_page, withoutLoader: true })).unwrap();
        return resp;
    } catch (error) {
        // Maneja otros errores
        console.error(error);
        return thunkAPI.rejectWithValue('Error de red');
    }
});

// Acciion asincrona para actualizar asignacion de prospecto
export const updateCSAssignation = createAsyncThunk<
    ProspectResponse,
    { prospect_id: number; assigned_id: number },
    {
        state: RootState;
        rejectValue: string;
    }
>('prospects/updateCSAssignation', async ({ prospect_id, assigned_id }, thunkAPI) => {
    try {
        const {
            User: { token },
        } = thunkAPI.getState();
        const prospect = thunkAPI.getState().prospects.list.find((prospect) => prospect.id === prospect_id);
        if (prospect?.assigned_user_id) {
            console.error('El prospecto ya tiene un CS asignado');
            return thunkAPI.rejectWithValue(null);
        }
        return API.updateCSAssignation({ token, prospect_id, assigned_id });
    } catch (error) {
        // Maneja otros errores
        console.error(error);
        return thunkAPI.rejectWithValue('Error de red');
    }
});

// Acción asíncrona para actualizar recomendación de prospectos
export const updateRecommendation = createAsyncThunk<
    ProspectResponse,
    { prospect_id: number; recommendation_id: number },
    {
        state: RootState;
        rejectValue: string;
    }
>('prospects/updateRecommendation', async ({ prospect_id, recommendation_id }, thunkAPI) => {
    try {
        const {
            User: { token },
        } = thunkAPI.getState();
        // validate if
        const resp = API.updateRecommendation({ token, prospect_id, recommendation_id });

        return resp;
    } catch (error) {
        // Maneja otros errores
        console.error(error);
        return thunkAPI.rejectWithValue('Error de red');
    }
});

// Acción asíncrona para actualizar atención de prospectos
export const updateAttention = createAsyncThunk<
    ProspectResponse,
    { prospect_id: number; attention_id: number },
    {
        state: RootState;
        rejectValue: string;
    }
>('prospects/updateAttention', async ({ prospect_id, attention_id }, thunkAPI) => {
    try {
        const {
            User: { token },
        } = thunkAPI.getState();
        return API.updateAttention({ token, prospect_id, attention_id });
    } catch (error) {
        // Maneja otros errores
        console.error(error);
        return thunkAPI.rejectWithValue('Error de red');
    }
});

// Acción asíncrona para actualizar notas de prospectos
export const updateNoteProspect = createAsyncThunk<
    ProspectResponseNote,
    { prospect_id: number; note: string },
    {
        state: RootState;
        rejectValue: string;
    }
>('prospects/updateNotes', async ({ prospect_id, note }, thunkAPI) => {
    try {
        const {
            User: { token, name },
        } = thunkAPI.getState();
        return {
            ...API.updateNoteProspect({ token, prospect_id, note }),
            note_updated_at: new Date(),
            note_updated_by_fullname: name,
        };
    } catch (error) {
        // Maneja otros errores
        console.error(error);
        return thunkAPI.rejectWithValue('Error de red');
    }
});
